import { useState, useEffect } from "react";
import { api, detailText, formatMediaUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles,
  Upload,
  Image as ImageIcon,
  Check,
  Loader2,
  Download,
  Share2,
  Trash2,
  User,
  Heart,
  Layers,
  Wand2,
  RefreshCw,
  Eye,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const POSTURE_OPTIONS = ["Standar", "Ramping / Slim", "Atletis", "Berisi / Curvy"];

export default function AiPrewedding() {
  const [activeTab, setActiveTab] = useState("studio"); // 'studio' | 'history'
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("Semua");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Form input foto mempelai
  const [groomPhotos, setGroomPhotos] = useState([null, null]);
  const [groomPreviews, setGroomPreviews] = useState([null, null]);
  const [bridePhotos, setBridePhotos] = useState([null, null]);
  const [bridePreviews, setBridePreviews] = useState([null, null]);

  // Form data fisik
  const [groomHeight, setGroomHeight] = useState("175");
  const [groomWeight, setGroomWeight] = useState("68");
  const [brideHeight, setBrideHeight] = useState("162");
  const [brideWeight, setBrideWeight] = useState("52");

  // State proses generate
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState("");
  const [latestResult, setLatestResult] = useState(null);
  const [applyingGallery, setApplyingGallery] = useState(false);

  // Riwayat generate
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await api.get("/ai/prewedding-templates");
      const list = res.data?.templates || [];
      setTemplates(list);
      if (list.length > 0 && !selectedTemplateId) {
        setSelectedTemplateId(list[0].template_id);
      }
    } catch (err) {
      toast.error("Gagal memuat katalog template AI");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get("/ai/prewedding/generations");
      setHistory(res.data?.generations || []);
    } catch (err) {
      toast.error("Gagal memuat riwayat foto AI");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    }
  }, [activeTab]);

  const handlePhotoSelect = (gender, index, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran foto maksimal 5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      if (gender === "groom") {
        const nextPhotos = [...groomPhotos];
        nextPhotos[index] = file;
        setGroomPhotos(nextPhotos);

        const nextPrev = [...groomPreviews];
        nextPrev[index] = e.target.result;
        setGroomPreviews(nextPrev);
      } else {
        const nextPhotos = [...bridePhotos];
        nextPhotos[index] = file;
        setBridePhotos(nextPhotos);

        const nextPrev = [...bridePreviews];
        nextPrev[index] = e.target.result;
        setBridePreviews(nextPrev);
      }
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (gender, index) => {
    if (gender === "groom") {
      const nextPhotos = [...groomPhotos];
      nextPhotos[index] = null;
      setGroomPhotos(nextPhotos);

      const nextPrev = [...groomPreviews];
      nextPrev[index] = null;
      setGroomPreviews(nextPrev);
    } else {
      const nextPhotos = [...bridePhotos];
      nextPhotos[index] = null;
      setBridePhotos(nextPhotos);

      const nextPrev = [...bridePreviews];
      nextPrev[index] = null;
      setBridePreviews(nextPrev);
    }
  };

  const handleGenerate = async () => {
    if (!groomPhotos[0] || !bridePhotos[0]) {
      toast.error("Wajib mengunggah minimal 1 foto utama untuk mempelai pria dan wanita");
      return;
    }
    if (!selectedTemplateId) {
      toast.error("Pilih salah satu template prewedding");
      return;
    }

    const formData = new FormData();
    formData.append("template_id", selectedTemplateId);
    formData.append("groom_height", groomHeight);
    formData.append("groom_weight", groomWeight);
    formData.append("bride_height", brideHeight);
    formData.append("bride_weight", brideWeight);
    formData.append("groom_photo_1", groomPhotos[0]);
    if (groomPhotos[1]) formData.append("groom_photo_2", groomPhotos[1]);
    formData.append("bride_photo_1", bridePhotos[0]);
    if (bridePhotos[1]) formData.append("bride_photo_2", bridePhotos[1]);

    setGenerating(true);
    setGenStep("Menganalisis wajah & pose calon mempelai...");

    const stepTimer1 = setTimeout(() => {
      setGenStep("Menyusun komposisi busana & tata cahaya Gemini AI...");
    }, 4000);

    const stepTimer2 = setTimeout(() => {
      setGenStep("Merender foto prewedding resolusi tinggi...");
    }, 12000);

    try {
      const res = await api.post("/ai/generate-prewedding", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setLatestResult(res.data);
      toast.success("Foto Prewedding AI berhasil dihasilkan!");
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal menghasilkan foto AI. Silakan coba kembali.");
    } finally {
      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);
      setGenerating(false);
      setGenStep("");
    }
  };

  const handleApplyToGallery = async (generationId) => {
    if (!generationId) return;
    setApplyingGallery(true);
    try {
      const res = await api.post("/ai/prewedding/apply-to-gallery", {
        generation_id: generationId,
      });
      toast.success(res.data?.message || "Berhasil dipasang ke galeri undangan!");
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal memasang ke galeri");
    } finally {
      setApplyingGallery(false);
    }
  };

  const categories = ["Semua", ...Array.from(new Set(templates.map((t) => t.category).filter(Boolean)))];
  const filteredTemplates = selectedCategory === "Semua"
    ? templates
    : templates.filter((t) => t.category === selectedCategory);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      {/* Header Banner */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-border/60">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="p-1.5 rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <Wand2 className="h-5 w-5" />
            </span>
            <h1 className="font-serif text-2xl md:text-3xl font-semibold">AI Prewedding Studio</h1>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">
            Hasilkan foto prewedding studio profesional secara instan menggunakan kecerdasan buatan Gemini AI.
          </p>
        </div>

        {/* Tab Toggle */}
        <div className="flex items-center p-1 rounded-xl bg-muted/60 border border-border/60">
          <button
            type="button"
            onClick={() => setActiveTab("studio")}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeTab === "studio" ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Studio Generator
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("history")}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeTab === "history" ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Riwayat Generate ({history.length})
          </button>
        </div>
      </div>

      {activeTab === "studio" ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Kolom Kiri: Form Upload Foto & Parameter Fisik (lg:col-span-5) */}
          <div className="lg:col-span-5 space-y-5">
            {/* Box Upload Foto Pria */}
            <Card className="rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm p-4 md:p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-sky-500/15 text-sky-600">
                    <User className="h-4 w-4" />
                  </div>
                  <h3 className="text-sm font-semibold">Foto Mempelai Pria</h3>
                </div>
                <Badge variant="secondary" className="text-[10px]">2 Foto Rekomendasi</Badge>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[0, 1].map((idx) => (
                  <div key={idx} className="relative">
                    {groomPreviews[idx] ? (
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden border border-border shadow-inner group">
                        <img src={groomPreviews[idx]} alt="Pria" className="h-full w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removePhoto("groom", idx)}
                          className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/60 text-white hover:bg-destructive transition-colors cursor-pointer"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center aspect-[3/4] rounded-xl border border-dashed border-border/80 hover:border-primary/50 hover:bg-primary/5 bg-muted/20 transition-all cursor-pointer p-2 text-center">
                        <Upload className="h-5 w-5 text-muted-foreground mb-1" />
                        <span className="text-[11px] font-medium text-foreground">
                          {idx === 0 ? "Foto Wajah Depan" : "Foto Samping / Bebas"}
                        </span>
                        <span className="text-[9px] text-muted-foreground mt-0.5">JPG / PNG maks 5MB</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoSelect("groom", idx, e.target.files?.[0])}
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>

              {/* Form Data Fisik Pria */}
              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-border/40">
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground block mb-1">Tinggi Badan Pria</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={groomHeight}
                      onChange={(e) => setGroomHeight(e.target.value)}
                      placeholder="175"
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-border bg-background"
                    />
                    <span className="absolute right-2.5 top-1.5 text-[10px] text-muted-foreground">cm</span>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground block mb-1">Berat Badan Pria</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={groomWeight}
                      onChange={(e) => setGroomWeight(e.target.value)}
                      placeholder="68"
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-border bg-background"
                    />
                    <span className="absolute right-2.5 top-1.5 text-[10px] text-muted-foreground">kg</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Box Upload Foto Wanita */}
            <Card className="rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm p-4 md:p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-rose-500/15 text-rose-600">
                    <Heart className="h-4 w-4" />
                  </div>
                  <h3 className="text-sm font-semibold">Foto Mempelai Wanita</h3>
                </div>
                <Badge variant="secondary" className="text-[10px]">2 Foto Rekomendasi</Badge>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[0, 1].map((idx) => (
                  <div key={idx} className="relative">
                    {bridePreviews[idx] ? (
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden border border-border shadow-inner group">
                        <img src={bridePreviews[idx]} alt="Wanita" className="h-full w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removePhoto("bride", idx)}
                          className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/60 text-white hover:bg-destructive transition-colors cursor-pointer"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center aspect-[3/4] rounded-xl border border-dashed border-border/80 hover:border-primary/50 hover:bg-primary/5 bg-muted/20 transition-all cursor-pointer p-2 text-center">
                        <Upload className="h-5 w-5 text-muted-foreground mb-1" />
                        <span className="text-[11px] font-medium text-foreground">
                          {idx === 0 ? "Foto Wajah Depan" : "Foto Samping / Bebas"}
                        </span>
                        <span className="text-[9px] text-muted-foreground mt-0.5">JPG / PNG maks 5MB</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoSelect("bride", idx, e.target.files?.[0])}
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>

              {/* Form Data Fisik Wanita */}
              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-border/40">
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground block mb-1">Tinggi Badan Wanita</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={brideHeight}
                      onChange={(e) => setBrideHeight(e.target.value)}
                      placeholder="162"
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-border bg-background"
                    />
                    <span className="absolute right-2.5 top-1.5 text-[10px] text-muted-foreground">cm</span>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground block mb-1">Berat Badan Wanita</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={brideWeight}
                      onChange={(e) => setBrideWeight(e.target.value)}
                      placeholder="52"
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-border bg-background"
                    />
                    <span className="absolute right-2.5 top-1.5 text-[10px] text-muted-foreground">kg</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Kolom Kanan: Katalog Template & Aksi Generate (lg:col-span-7) */}
          <div className="lg:col-span-7 space-y-5">
            <Card className="rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm p-4 md:p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-base font-semibold">Pilih Gaya &amp; Template Pose</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Template dikurasi oleh admin lengkap dengan sample &amp; prompt AI.
                  </p>
                </div>

                {/* Kategori Pills */}
                <div className="flex flex-wrap gap-1.5">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                        selectedCategory === cat
                          ? "bg-primary text-primary-foreground font-semibold"
                          : "bg-muted/60 text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid Templates */}
              {loadingTemplates ? (
                <div className="py-12 flex justify-center items-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 max-h-[380px] overflow-y-auto pr-1">
                  {filteredTemplates.map((t) => {
                    const isSelected = selectedTemplateId === t.template_id;
                    return (
                      <div
                        key={t.template_id}
                        onClick={() => setSelectedTemplateId(t.template_id)}
                        className={`relative rounded-xl border p-3 cursor-pointer transition-all duration-200 flex flex-col justify-between ${
                          isSelected
                            ? "border-primary bg-primary/[0.06] ring-2 ring-primary/30 shadow-md"
                            : "border-border/60 hover:border-primary/40 bg-card/50"
                        }`}
                      >
                        <div>
                          <div className="relative aspect-[16/10] rounded-lg overflow-hidden bg-muted mb-2.5">
                            {t.has_sample && t.sample_url ? (
                              <img src={formatMediaUrl(t.sample_url)} alt={t.title} className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex flex-col items-center justify-center text-muted-foreground/60 bg-gradient-to-br from-amber-500/10 to-primary/10">
                                <Sparkles className="h-6 w-6 mb-1 text-primary/40" />
                                <span className="text-[10px] font-medium">{t.category}</span>
                              </div>
                            )}
                            <Badge className="absolute top-2 left-2 text-[9px] px-1.5 py-0.5 bg-black/60 backdrop-blur-md text-white border-none">
                              {t.category}
                            </Badge>
                          </div>
                          <h4 className="text-xs font-bold leading-tight line-clamp-1">{t.title}</h4>
                          <p className="text-[11px] text-muted-foreground line-clamp-2 mt-1 leading-relaxed">
                            {t.description || "Gaya foto prewedding berkelas dengan pencahayaan studio profesional."}
                          </p>
                        </div>

                        {isSelected && (
                          <div className="mt-2 pt-2 border-t border-primary/20 flex items-center gap-1 text-[11px] font-semibold text-primary">
                            <Check className="h-3.5 w-3.5" /> Terpilih
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Tombol Generate Action */}
              <div className="mt-6 pt-4 border-t border-border/60">
                <Button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating || !groomPhotos[0] || !bridePhotos[0]}
                  className="w-full py-6 rounded-xl font-bold text-sm bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg transition-all duration-300 disabled:opacity-50"
                >
                  {generating ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>{genStep || "Sedang memproses AI..."}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5" />
                      <span>Generate Foto Prewedding AI</span>
                    </div>
                  )}
                </Button>
                <p className="text-[11px] text-center text-muted-foreground mt-2">
                  Ditenagai model visual AI Gemini &bull; Foto diproses dengan privasi aman
                </p>
              </div>
            </Card>

            {/* Panel Hasil Generate Terkini */}
            {latestResult && (
              <Card className="rounded-2xl border-amber-500/30 bg-gradient-to-b from-amber-500/[0.07] to-card p-5 shadow-xl animate-in fade-in-0 duration-300">
                <div className="flex items-center justify-between pb-3 border-b border-border/60 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-ping" />
                    <h3 className="text-sm font-bold text-foreground">Hasil Foto Prewedding AI</h3>
                  </div>
                  <Badge variant="outline" className="text-xs font-semibold text-amber-600 dark:text-amber-400 border-amber-500/30">
                    {latestResult.template_title}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-12 gap-5 items-center">
                  <div className="sm:col-span-6 relative aspect-[3/4] rounded-2xl overflow-hidden border border-border shadow-2xl bg-black">
                    <img src={formatMediaUrl(latestResult.image_url)} alt="Hasil AI" className="h-full w-full object-cover" />
                  </div>

                  <div className="sm:col-span-6 space-y-3">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Foto prewedding Anda siap digunakan! Pasang langsung ke galeri undangan digital atau simpan ke perangkat Anda.
                    </p>

                    <Button
                      type="button"
                      onClick={() => handleApplyToGallery(latestResult.generation_id)}
                      disabled={applyingGallery}
                      className="w-full rounded-xl bg-primary text-primary-foreground font-semibold text-xs py-5"
                    >
                      {applyingGallery ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      ) : (
                        <Layers className="h-4 w-4 mr-1.5" />
                      )}
                      Pasang ke Galeri Undangan
                    </Button>

                    <a
                      href={formatMediaUrl(latestResult.image_url)}
                      download="prewedding_ai.jpg"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-border/80 bg-background hover:bg-accent text-xs font-semibold transition-colors shadow-sm"
                    >
                      <Download className="h-4 w-4" /> Unduh Foto HD
                    </a>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      ) : (
        /* Tab Riwayat Generate */
        <div className="space-y-4">
          {loadingHistory ? (
            <div className="py-16 flex justify-center items-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-16 px-4 rounded-2xl border border-dashed border-border bg-card/30">
              <ImageIcon className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-foreground">Belum Ada Riwayat Foto AI</h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                Unggah foto Anda dan buat foto prewedding pertama di tab Studio Generator.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveTab("studio")}
                className="mt-4 rounded-xl text-xs"
              >
                Mulai Buat Foto
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {history.map((item) => (
                <Card key={item.generation_id} className="overflow-hidden rounded-2xl border-border/60 bg-card/80 shadow-sm flex flex-col justify-between">
                  <div className="relative aspect-[3/4] overflow-hidden bg-muted">
                    <img src={formatMediaUrl(item.image_url)} alt={item.template_title} className="h-full w-full object-cover" />
                    <Badge className="absolute top-2 left-2 text-[9px] bg-black/60 backdrop-blur-md text-white border-none">
                      {item.category || "Studio"}
                    </Badge>
                  </div>
                  <div className="p-3.5 space-y-2">
                    <h4 className="text-xs font-bold truncate">{item.template_title}</h4>
                    <div className="flex items-center gap-1.5 pt-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleApplyToGallery(item.generation_id)}
                        className="flex-1 text-[11px] h-8 rounded-lg"
                      >
                        Pasang Galeri
                      </Button>
                      <a
                        href={formatMediaUrl(item.image_url)}
                        download="prewedding_ai.jpg"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg border hover:bg-accent text-foreground transition-colors"
                        title="Unduh foto"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
